import { TrackItem } from '../types'
import {
  addTrackSegment,
  insertAfterTrack,
  insertBeforeTrack,
  remove,
} from './track-action'
import { getConflictSegment } from './track-insert-position'

/**
 * 合并轨道
 */
export function mergeTracks<T extends TrackItem>(
  target: T[],
  source: T[],
  options: {
    isMainTrack: (track: T, data: T[]) => boolean
    isPinnedTrack: (track: T) => boolean
    addSegment?: typeof addTrackSegment
  },
): T[] {
  const { isMainTrack, isPinnedTrack, addSegment = addTrackSegment } = options
  const base = [...target]
  if (source.length === 0) {
    return base
  }
  source = [...source]

  const getMainTrack = (data: T[]): T | null => {
    return data.find((t) => isMainTrack(t, data)) ?? null
  }

  const baseMainTrack = getMainTrack(base)
  const basePinnedTrack = base.find((t) => isPinnedTrack(t))
  const mainTrack = getMainTrack(source)
  const sourcePinnedTrack = source.find((t) => isPinnedTrack(t))

  if (!baseMainTrack || base.length === 0) {
    return source
  }

  if (!mainTrack) {
    console.error(`source tracks no video track exists.`, source)
    return target
  }

  // 主轨道
  mainTrack.segments.forEach((s) => {
    addSegment(s, baseMainTrack)
  })

  // 置顶轨道
  if (basePinnedTrack && sourcePinnedTrack) {
    sourcePinnedTrack.segments.forEach((s) => {
      addSegment(s, basePinnedTrack)
    })
    remove(sourcePinnedTrack, source)
  }

  const sourceMainTrackIndex = source.findIndex(
    (track) => track.id === mainTrack.id,
  )

  // 主轨道下方
  let currentStartMergedAudioTrackIndex = sourceMainTrackIndex + 1
  // 上次命中的轨道索引
  let prevMergedTargetTrack: T | null = null
  for (let index = sourceMainTrackIndex + 1; index < source.length; index++) {
    const t = source[index]!

    let currentStartMergedTrack = base[currentStartMergedAudioTrackIndex]

    while (currentStartMergedTrack) {
      if (currentStartMergedTrack.type === t.type) {
        // 相同轨道类型，验证是否会重叠覆盖
        const firstSegment = [...(t.segments ?? [])].sort(
          (a, b) => a.start - b.start,
        )[0]

        if (
          firstSegment &&
          !getConflictSegment(firstSegment, currentStartMergedTrack)
        ) {
          const _currentStartMergedTrack = currentStartMergedTrack

          t.segments.forEach((s) => {
            addSegment(s, _currentStartMergedTrack)
          })

          break
        }
      }

      currentStartMergedAudioTrackIndex++
      currentStartMergedTrack = base[currentStartMergedAudioTrackIndex]
    }

    // 命中了已经有的轨道
    if (currentStartMergedTrack) {
      prevMergedTargetTrack = currentStartMergedTrack
      currentStartMergedAudioTrackIndex++
      // eslint-disable-next-line no-continue
      continue
    }

    const targetTrack = prevMergedTargetTrack
    insertAfterTrack(t, targetTrack ?? baseMainTrack, base)
    prevMergedTargetTrack = t
    currentStartMergedAudioTrackIndex = base.length
  }

  // 主轨道上方
  const mergedMainTrackIndex = base.findIndex(
    (track) => track.id === baseMainTrack.id,
  )

  // 当前可以开始检查插入轨道的索引
  let currentStartMergedTrackIndex = mergedMainTrackIndex - 1
  // 上次命中的轨道索引
  let prevTargetTrack: T | null = null

  for (let index = sourceMainTrackIndex - 1; index >= 0; index--) {
    const t = source[index]!

    let currentStartMergedTrack = base[currentStartMergedTrackIndex]

    while (currentStartMergedTrack) {
      if (currentStartMergedTrack.type === t.type) {
        // 相同轨道类型，验证是否会重叠覆盖
        const firstSegment = [...(t.segments ?? [])].sort(
          (a, b) => a.start - b.start,
        )[0]

        if (
          firstSegment &&
          !getConflictSegment(firstSegment, currentStartMergedTrack)
        ) {
          const _currentStartMergedTrack = currentStartMergedTrack
          // 相同轨道类型，合并
          t.segments.forEach((s) => {
            addSegment(s, _currentStartMergedTrack)
          })

          break
        }
      }
      currentStartMergedTrackIndex--
      currentStartMergedTrack = base[currentStartMergedTrackIndex]
    }

    // 命中了已经有的轨道
    if (currentStartMergedTrack) {
      prevTargetTrack = currentStartMergedTrack
      currentStartMergedTrackIndex--
      // eslint-disable-next-line no-continue
      continue
    }

    // 没有命中已经有的轨道
    const targetTrack = isPinnedTrack(t) ? base[0] : prevTargetTrack
    insertBeforeTrack(t, targetTrack ?? baseMainTrack, base)
    prevTargetTrack = t
    // 0314修改，一个轨道没有命中，后面的轨道也可以命中，之前设置成-1，后面的轨道全都不命中了
    // currentStartMergedTrackIndex = -1
    currentStartMergedTrackIndex = base.findIndex((track) => track.id === t.id)
    currentStartMergedTrackIndex--
  }

  return base
}
